import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import Pagination from '../../../components/common/Pagination';
import ViewToggle, { readViewPreference } from '../../../components/common/ViewToggle';
import CourseNameBadge from '../../../components/common/CourseNameBadge';
import EnquiriesAnalytics from '../../../components/analytics/EnquiriesAnalyticsV2';

const SOURCES = [
  { value:'walk_in',      label:'Walk-in'       },
  { value:'website',      label:'Website'       },
  { value:'referral',     label:'Referral'      },
  { value:'social_media', label:'Social Media'  },
  { value:'phone',        label:'Phone'         },
  { value:'email',       label:'Email'         },
  { value:'other',       label:'Other'         },
];

const STATUSES = [
  { value:'new',             label:'New',             color:'primary'   },
  { value:'contacted',      label:'Contacted',      color:'info'      },
  { value:'follow_up',      label:'Follow Up',      color:'warning'   },
  { value:'enrolled',       label:'Enrolled',        color:'success'   },
  { value:'not_interested', label:'Not Interested', color:'secondary' },
  { value:'lost',           label:'Lost',            color:'danger'    },
  { value:'closed',         label:'Closed',          color:'dark'      },
];

const ENROLL_STATUSES = [
  { value:'interested',    label:'Interested'    },
  { value:'not_interested',label:'Not Interested' },
  { value:'waiting',       label:'Waiting'      },
  { value:'joined',        label:'Joined'       },
];

const PERSON_ARRIVED = [
  { value:'student',  label:'Student'  },
  { value:'parent',    label:'Parent'   },
  { value:'guardian', label:'Guardian' },
  { value:'other',    label:'Other'    },
];

const OUTCOMES = [
  { value:'interested',     label:'Interested'     },
  { value:'not_interested', label:'Not Interested' },
  { value:'callback',       label:'Callback'       },
  { value:'enrolled',       label:'Enrolled'       },
  { value:'no_response',    label:'No Response'    },
  { value:'other',          label:'Other'          },
];

const FU_METHODS = [
  { value:'call',     label:'Call'      },
  { value:'whatsapp', label:'WhatsApp'  },
  { value:'email',    label:'Email'     },
  { value:'visit',    label:'Visit'     },
  { value:'other',    label:'Other'     },
];

const statusColor = (s) => STATUSES.find(x=>x.value===s)?.color || 'secondary';
const statusLabel = (s) => STATUSES.find(x=>x.value===s)?.label || s || '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN') : '—';
const isOverdue = (d) => d && new Date(d) < new Date();
const isDueSoon = (d) => { if (!d) return false; const diff=(new Date(d)-new Date())/(1000*60*60*24); return diff>=0&&diff<=3; };

const buildCountOptions = (values = []) => {
  const counts = new Map();
  values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .forEach((value) => counts.set(value, Number(counts.get(value) || 0) + 1));
  return Array.from(counts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, label: `${value} (${count})` }));
};

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

const FollowUpDialog = ({ show, enquiry, onClose, onSuccess, onAlert }) => {
  const [form, setForm] = useState({ method:'call', notes:'', nextFollowUp:'', outcome:'callback' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if(show) setForm({ method:'call', notes:'', nextFollowUp:'', outcome:'callback' }); }, [show]);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/training/enquiries/${enquiry._id}/follow-up`, form);
      if (r.data?.success !== false) { onSuccess(r.data?.data||r.data); onClose(); }
      else onAlert(r.data?.message||'Failed.','danger');
    } catch(e) { onAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title={`Add Follow-up — ${enquiry?.name}`} size="modal-md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving?<><span className="spinner-border spinner-border-sm me-1"/>Saving…</>:'Record Follow-up'}
          </button>
        </>
      }
    >
      <div className="row g-3">
        <div className="col-6">
          <label className="form-label">Method</label>
          <select className="form-select" value={form.method} onChange={e=>set('method',e.target.value)}>
            {FU_METHODS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="col-6">
          <label className="form-label">Outcome</label>
          <select className="form-select" value={form.outcome} onChange={e=>set('outcome',e.target.value)}>
            {OUTCOMES.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={3} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="What was discussed…" />
        </div>
        <div className="col-12">
          <label className="form-label">Next Follow-up Date</label>
          <input className="form-control" type="date" value={form.nextFollowUp} onChange={e=>set('nextFollowUp',e.target.value)} />
        </div>
      </div>
    </BSModal>
  );
};

const DetailDialog = ({ show, enquiry, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!show || !enquiry) return;
    api.get(`/training/enquiries/${enquiry._id}`)
      .then(r => setData(r.data?.data||r.data))
      .catch(()=>{});
    return () => setData(null);
  }, [show, enquiry]);

  const fus = data?.followUps || [];

  return (
    <BSModal show={show} onClose={onClose} title={`Enquiry Detail — ${enquiry?.enquiryNumber||''}`} size="modal-lg">
      {!data ? (
        <div className="text-center py-4"><div className="spinner-border text-primary"/></div>
      ) : (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4"><span className="text-muted small">Date</span><div className="fw-semibold">{fmtDate(data.enquiryDate)}</div></div>
            <div className="col-4"><span className="text-muted small">Name</span><div className="fw-semibold">{data.name}</div></div>
            <div className="col-4"><span className="text-muted small">Area</span><div>{data.area||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Address</span><div>{data.address||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Contact</span><div>{data.phone||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Email</span><div className="text-break">{data.email||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Course</span><div><CourseNameBadge courseName={data.course?.courseName || data.course?.name || data.courseName || '—'} courseColor={data.course?.courseColor} compact /></div></div>
            <div className="col-4"><span className="text-muted small">Source</span><div>{SOURCES.find(s=>s.value===data.source)?.label||data.source}</div></div>
            <div className="col-4"><span className="text-muted small">Enroll Status</span>
              <div><span className={`badge bg-${data.enrollStatus==='interested'?'success':data.enrollStatus==='not_interested'?'secondary':data.enrollStatus==='waiting'?'warning':'info'}`}>
                {ENROLL_STATUSES.find(e=>e.value===data.enrollStatus)?.label||data.enrollStatus}
              </span></div>
            </div>
            <div className="col-4"><span className="text-muted small">Joined</span>
              <div><span className={`badge bg-${data.joined?'success':'secondary'}`}>{data.joined?'Yes':'No'}</span></div>
            </div>
            <div className="col-4"><span className="text-muted small">Person Arrived</span><div>{PERSON_ARRIVED.find(p=>p.value===data.personArrived)?.label||data.personArrived}</div></div>
            <div className="col-4"><span className="text-muted small">Counselled By</span><div>{data.conselledBy?.firstName?' '+data.conselledBy.firstName+' '+data.conselledBy.lastName:data.conselledByOther||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Degree</span><div>{data.degree||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Department</span><div>{data.department||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">College</span><div>{data.collegeName||'—'}</div></div>
            <div className="col-4"><span className="text-muted small">Status</span>
              <div><span className={`badge bg-${statusColor(data.status)}`}>{statusLabel(data.status)}</span></div>
            </div>
            <div className="col-4"><span className="text-muted small">Next Follow-up</span>
              <div className={isOverdue(data.nextFollowUp)?'text-danger fw-semibold':isDueSoon(data.nextFollowUp)?'text-warning fw-semibold':''}>
                {fmtDate(data.nextFollowUp)}
              </div>
            </div>
            {data.conversation && <div className="col-12"><span className="text-muted small">Conversation</span><div>{data.conversation}</div></div>}
            {data.notes && <div className="col-12"><span className="text-muted small">Notes</span><div>{data.notes}</div></div>}
          </div>
          <hr />
          <h6 className="fw-bold mb-2">Follow-up History ({fus.length})</h6>
          {fus.length === 0 ? (
            <div className="text-muted small">No follow-ups recorded.</div>
          ) : (
            <div className="timeline">
              {[...fus].reverse().map((fu, i) => (
                <div key={i} className="d-flex gap-3 mb-3">
                  <div className="text-center" style={{minWidth:36}}>
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{width:32,height:32,fontSize:12}}>
                      {i+1}
                    </div>
                    {i < fus.length-1 && <div className="border-start border-2 mx-auto" style={{height:24,width:0}}/>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex gap-2 flex-wrap align-items-center mb-1">
                      <span className="badge bg-light text-dark border">{FU_METHODS.find(m=>m.value===fu.method)?.label||fu.method}</span>
                      <span className={`badge bg-${fu.outcome==='enrolled'?'success':fu.outcome==='not_interested'?'secondary':'info'}`}>
                        {OUTCOMES.find(o=>o.value===fu.outcome)?.label||fu.outcome}
                      </span>
                      <span className="text-muted small">{fmtDateTime(fu.date)}</span>
                      {fu.conductedBy && <span className="text-muted small">by {fu.conductedBy.firstName} {fu.conductedBy.lastName}</span>}
                    </div>
                    {fu.notes && <p className="small mb-1">{fu.notes}</p>}
                    {fu.nextFollowUp && <div className="small text-muted">Next: {fmtDate(fu.nextFollowUp)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </BSModal>
  );
};

const ImportDialog = ({ show, onClose, onSuccess }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setParseResult(null);
      setColumnMapping({});
      setPreviewData([]);
      setImporting(false);
    }
  }, [show]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files?.[0]) validateAndUpload(files[0]);
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files?.[0]) validateAndUpload(files[0]);
  };

  const validateAndUpload = async (f) => {
    if (!f) return;
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
      alert('Please upload a valid Excel or CSV file');
      return;
    }
    setFile(f);

    const formData = new FormData();
    formData.append('file', f);
    formData.append('module', 'enquiry');

    try {
      const res = await api.post('/training/data-migration/upload', formData, true);
      if (res.data?.success !== false) {
        const data = res.data?.data || res.data;
        setParseResult({
          headers: data.headers || [],
          totalRows: data.totalRows || 0,
          previewRows: data.previewRows || [],
          rows: data.rows || [],
        });
        setColumnMapping(data.columnMapping || {});
      } else {
        alert(res.data?.message || 'Failed to parse file');
        setFile(null);
      }
    } catch (err) {
      alert('Parse failed: ' + (err.response?.data?.message || err.message));
      setFile(null);
    }
  };

  const handlePreview = () => {
    if (!parseResult?.rows?.length) return;
    
    // Helper to parse date string as DD/MM/YYYY
    const parsePreviewDate = (value) => {
      if (!value) return null;
      const match = String(value).match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (match) {
        return {
          day: parseInt(match[1], 10),
          month: parseInt(match[2], 10),
          year: parseInt(match[3], 10)
        };
      }
      return null;
    };

    const mapped = parseResult.rows.slice(0, 5).map((row, idx) => {
      const item = {};
      Object.entries(columnMapping).forEach(([field, header]) => {
        const colIndex = parseResult.headers.indexOf(header);
        if (colIndex >= 0 && colIndex < row.length) {
          item[field] = row[colIndex];
        }
      });
      
      // Calculate year and month from date for preview
      if (item.enquiryDate) {
        const parsed = parsePreviewDate(item.enquiryDate);
        if (parsed) {
          const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
          item.parsedYear = parsed.year;
          item.parsedMonth = months[parsed.month - 1] || parsed.month;
          item.parsedDay = parsed.day;
        }
      }
      
      return item;
    });
    setPreviewData(mapped);
  };

  const handleImport = async () => {
    if (!previewData.length || importing) return;
    setImporting(true);
    try {
      const indices = {};
      Object.entries(columnMapping).forEach(([field, header]) => {
        const colIndex = parseResult.headers.indexOf(header);
        if (colIndex >= 0) indices[field] = colIndex;
      });

      const res = await api.post('/training/data-migration/execute', {
        module: 'enquiry',
        columnMapping: indices,
        rows: parseResult.rows,
        fileName: file.name,
        originalFileName: file.name,
      });

      if (res.data?.success !== false) {
        const d = res.data?.data || res.data;
        onSuccess && onSuccess(d);
        onClose();
      } else {
        alert(res.data?.message || 'Import failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed');
    }
    setImporting(false);
  };

  const allFields = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'enquiryDate', label: 'Enquiry Date' },
    { key: 'courseName', label: 'Course' },
    { key: 'source', label: 'Source' },
    { key: 'area', label: 'Area' },
    { key: 'address', label: 'Address' },
    { key: 'degree', label: 'Degree' },
    { key: 'department', label: 'Department' },
    { key: 'collegeName', label: 'College' },
    { key: 'personArrived', label: 'Person Arrived' },
    { key: 'conversation', label: 'Conversation' },
    { key: 'status', label: 'Status' },
  ];

  return (
    <BSModal show={show} onClose={onClose} title="Import Enquiries" size="modal-lg">
      <div className="mb-3">
        <div
          className={`border rounded p-4 text-center ${dragActive ? 'border-primary bg-light' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{ cursor: 'pointer' }}
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} style={{ display: 'none' }} />
          <i className="bi bi-cloud-upload fs-2 text-muted mb-2 d-block" />
          <p className="mb-1 small text-muted">Drag & drop your Excel/CSV file here, or click to browse</p>
          <p className="mb-0 small text-muted">Supports .xlsx, .xls, .csv (max 50MB)</p>
          {file && <p className="mb-0 mt-2 fw-semibold"><i className="bi bi-file-earmark me-1"/>{file.name}</p>}
        </div>
      </div>

      {parseResult && (
        <>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Column Mapping</h6>
              <button className="btn btn-outline-primary btn-sm" onClick={handlePreview}>Preview</button>
            </div>
            <p className="small text-muted mb-2">Map Excel columns to enquiry fields. Total rows: {parseResult.totalRows}</p>
            <div className="row g-2">
              {allFields.map(f => (
                <div key={f.key} className="col-md-3"><label className="form-label small">{f.label}</label>
                  <select className="form-select form-select-sm" value={columnMapping[f.key] || ''} onChange={e => setColumnMapping(p => ({ ...p, [f.key]: e.target.value }))}>
                    <option value="">-- Skip --</option>
                    {parseResult.headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {previewData.length > 0 && (
            <div className="mb-3">
              <h6>Preview (first 5 rows) - Verify Year & Month are correct</h6>
              <div className="table-responsive" style={{ maxHeight: 200, overflow: 'auto' }}>
                <table className="table table-sm table-bordered small">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th><th>Name</th><th>Phone</th><th>Year</th><th>Month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((p, i) => (
                      <tr key={i}>
                        <td>{p.enquiryDate || '-'}</td>
                        <td>{p.name || '-'}</td>
                        <td>{p.phone || '-'}</td>
                        <td className={p.parsedYear ? (p.parsedYear >= 2024 && p.parsedYear <= 2026 ? 'text-success' : 'text-danger') : 'text-muted'}>
                          {p.parsedYear || '-'}
                        </td>
                        <td className={p.parsedMonth ? 'text-primary' : 'text-muted'}>
                          {p.parsedMonth || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={importing}>Cancel</button>
            <button className="btn btn-success btn-sm" onClick={handleImport} disabled={importing || !previewData.length}>
              {importing ? <><span className="spinner-border spinner-border-sm me-1"/>Importing…</> : 'Import All'}
            </button>
          </div>
        </>
      )}
    </BSModal>
  );
};

const blankForm = {
  enquiryDate:'', name:'', area:'', address:'', phone:'', email:'',
  courseId:'', courseName:'', source:'walk_in',
  enrollStatus:'interested', joined:false, conversation:'',
  conselledBy:'', conselledByOther:'',
  degree:'', department:'', collegeName:'', personArrived:'student',
  notes:'', nextFollowUp:''
};

export default function Enquiries() {
  const { user, role, canAccessModule, hasPermission } = useAuth();
  const roleKey = role?.key || user?.roleKey || user?.role || '';
  const legacyModuleEnabled = canAccessModule('enquiries');
  const legacyWrite = legacyModuleEnabled && !['student', 'parent', 'viewer'].includes(roleKey);
  const canCreate = hasPermission('enquiries', 'create') || legacyWrite;
  const canEdit = hasPermission('enquiries', 'edit') || legacyWrite;
  const canAdmin = hasPermission('enquiries', 'delete') || roleKey === 'admin' || roleKey === 'tenant_admin';
  const canSeeAnalytics = canEdit || canAdmin;
  const navigate = useNavigate();

  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses]     = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [view, setView]           = useState(() => readViewPreference('enquiries', 'table'));

  const [showForm, setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(blankForm);
  const [saving, setSaving]       = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [search, setSearch]             = useState('');
  const [dueSoon, setDueSoon]           = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    lead: '',
    courseSource: '',
    pipeline: '',
    followFrom: '',
    followTo: '',
  });
  const [activeHeaderFilter, setActiveHeaderFilter] = useState('');
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);

  const [dlgFollowUp, setDlgFollowUp] = useState(null);
  const [dlgDetail,   setDlgDetail]   = useState(null);
  const [dlgImport,   setDlgImport]   = useState(false);
  const [alert, setAlert]             = useState({ show:false, msg:'', type:'success' });
  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  // Year filter tabs
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearStats, setYearStats] = useState(null);

  const MONTHS = [
    { value: 'January', label: 'Jan' },
    { value: 'February', label: 'Feb' },
    { value: 'March', label: 'Mar' },
    { value: 'April', label: 'Apr' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'Jun' },
    { value: 'July', label: 'Jul' },
    { value: 'August', label: 'Aug' },
    { value: 'September', label: 'Sep' },
    { value: 'October', label: 'Oct' },
    { value: 'November', label: 'Nov' },
    { value: 'December', label: 'Dec' },
  ];

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all enquiries${yearFilter !== 'all' ? ` for ${yearFilter}${monthFilter !== 'all' ? ` (${monthFilter})` : ''}` : ''}? This cannot be undone.`)) return;
    try {
      const params = {};
      if (yearFilter !== 'all') {
        params.year = yearFilter;
        if (monthFilter !== 'all') {
          params.month = monthFilter;
        }
      }
      const r = await api.delete('/training/enquiries', { data: params });
      if (r.data?.success !== false) {
        showAlert(`Deleted ${r.data?.data?.deletedCount || 0} enquiries`, 'success');
        fetchEnquiries();
        fetchYearStats();
        refreshStats();
      } else {
        showAlert(r.data?.message || 'Delete failed', 'danger');
      }
    } catch (err) {
      showAlert(err.response?.data?.message || 'Delete failed', 'danger');
    }
  };

  useEffect(() => {
    api.get('/training/courses', {params:{limit:200}}).then(r=>setCourses(Array.isArray(r.data?.records)?r.data.records:Array.isArray(r.data?.data)?r.data.data:[])).catch(()=>{});
    api.get('/training/enquiries/meta/counsellors').then(r=>setCounsellors(Array.isArray(r.data?.records)?r.data.records:Array.isArray(r.data?.data)?r.data.data:[])).catch(()=>{});
  }, []);

  const refreshStats = useCallback(() => {
    api.get('/training/enquiries/stats').then(r => setStats(r.data?.data || r.data || null)).catch(() => {});
  }, []);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page, 
        limit: 20, 
        search:search||undefined, 
        status:filterStatus||undefined, 
        source:filterSource||undefined, 
        dueSoon:dueSoon||undefined 
      };
      // Use API year/month filtering if selected
      if (yearFilter !== 'all') {
        params.year = yearFilter;
        if (monthFilter !== 'all') {
          params.month = monthFilter;
        }
      }
      const r = await api.get('/training/enquiries', { params });
      setEnquiries(r.data?.records||r.data?.data||[]);
      setTotalPages(r.data?.totalPages||1);
    } catch(_) {}
    setLoading(false);
  }, [page, search, filterStatus, filterSource, dueSoon, yearFilter, monthFilter]);

  const fetchYearStats = useCallback(async () => {
    try {
      const r = await api.get('/training/enquiries/stats/year-month');
      if (r.data?.success !== false) {
        setYearStats(r.data?.years ? r.data : (r.data?.data || null));
      } else {
        console.log('Year stats API error:', r.data);
      }
    } catch (e) {
      console.log('Year stats fetch error:', e);
    }
  }, []);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);
  useEffect(() => { fetchYearStats(); }, [fetchYearStats]);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  const enquiryColumnOptions = {
    lead: buildCountOptions(enquiries.flatMap((enq) => [enq.name || '', enq.phone || '', enq.email || '', enq.area || '', enq.enquiryNumber || ''])),
    courseSource: buildCountOptions(enquiries.flatMap((enq) => [enq.course?.courseName || enq.courseName || '', SOURCES.find((s) => s.value === enq.source)?.label || enq.source || '', enq.collegeName || '', enq.degree || ''])),
    pipeline: buildCountOptions(enquiries.flatMap((enq) => [statusLabel(enq.status), ENROLL_STATUSES.find((e) => e.value === enq.enrollStatus)?.label || enq.enrollStatus || '', enq.joined ? 'Joined' : 'Not Joined'])),
  };

  const filteredEnquiries = enquiries.filter((enq) => {
    const leadText = `${enq.name || ''} ${enq.phone || ''} ${enq.email || ''} ${enq.area || ''} ${enq.enquiryNumber || ''}`.toLowerCase();
    const courseSourceText = `${enq.course?.courseName || enq.courseName || ''} ${SOURCES.find(s=>s.value===enq.source)?.label || enq.source || ''} ${enq.collegeName || ''} ${enq.degree || ''}`.toLowerCase();
    const pipelineText = `${statusLabel(enq.status)} ${ENROLL_STATUSES.find(e=>e.value===enq.enrollStatus)?.label || enq.enrollStatus || ''} ${enq.joined ? 'joined' : 'not joined'}`.toLowerCase();
    const followDate = enq.nextFollowUp ? new Date(enq.nextFollowUp).toISOString().split('T')[0] : '';
    if (columnFilters.lead && !leadText.includes(columnFilters.lead.toLowerCase())) return false;
    if (columnFilters.courseSource && !courseSourceText.includes(columnFilters.courseSource.toLowerCase())) return false;
    if (columnFilters.pipeline && !pipelineText.includes(columnFilters.pipeline.toLowerCase())) return false;
    if (columnFilters.followFrom && (!followDate || followDate < columnFilters.followFrom)) return false;
    if (columnFilters.followTo && (!followDate || followDate > columnFilters.followTo)) return false;
    return true;
  });

  const setColumnFilter = (key, value) => setColumnFilters(prev => ({ ...prev, [key]: value }));
  const clearColumnFilters = () => {
    setColumnFilters({
      lead: '',
      courseSource: '',
      pipeline: '',
      followFrom: '',
      followTo: '',
    });
    setActiveHeaderFilter('');
  };

  const renderHeaderFilter = (key, label, type = 'text', options = []) => {
    const isActive = activeHeaderFilter === key;
    const hasValue = key === 'followUp' ? !!(columnFilters.followFrom || columnFilters.followTo) : !!columnFilters[key];
    const normalizedValue = (columnFilters[key] || '').toLowerCase();
    const filteredOptions = options.filter(option => !normalizedValue || String(option.label || option.value || '').toLowerCase().includes(normalizedValue));
    return (
      <div className="d-inline-flex align-items-center gap-1 position-relative">
        <span>{label}</span>
        <button
          type="button"
          className={`btn btn-sm p-0 border-0 bg-transparent ${hasValue ? 'text-primary' : 'text-muted'}`}
          style={{ width: 18, height: 18, lineHeight: 1 }}
          onClick={() => setActiveHeaderFilter(cur => cur === key ? '' : key)}
          title={`Filter ${label}`}
        >
          <i className={`bi ${hasValue ? 'bi-funnel-fill' : 'bi-funnel'}`} />
        </button>
        {isActive && (
          <div className="position-absolute bg-white border rounded-3 shadow-sm p-2" style={{ top: 'calc(100% + 6px)', left: 0, minWidth: 220, zIndex: 20 }}>
            {type === 'dateRange' ? (
              <div className="d-flex flex-column gap-2">
                <div>
                  <label className="form-label small text-muted mb-1">From</label>
                  <input type="date" className="form-control form-control-sm" value={columnFilters.followFrom} onChange={e => setColumnFilters(f => ({ ...f, followFrom: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small text-muted mb-1">To</label>
                  <input type="date" className="form-control form-control-sm" value={columnFilters.followTo} onChange={e => setColumnFilters(f => ({ ...f, followTo: e.target.value }))} />
                </div>
              </div>
            ) : (
              <>
                <input className="form-control form-control-sm" placeholder={`Filter ${label.toLowerCase()}`} value={columnFilters[key]} onChange={e => setColumnFilter(key, e.target.value)} />
                <div className="list-group list-group-flush border rounded-2 mt-2 overflow-auto" style={{ maxHeight: 180 }}>
                  {filteredOptions.length ? filteredOptions.map(option => (
                    <button key={option.value} type="button" className="list-group-item list-group-item-action text-start py-2 px-2" onClick={() => { setColumnFilter(key, option.value); setActiveHeaderFilter(''); }}>
                      {option.label}
                    </button>
                  )) : <div className="small text-muted px-2 py-2">No suggestions</div>}
                </div>
              </>
            )}
            <div className="d-flex justify-content-between mt-2">
              <button type="button" className="btn btn-link btn-sm text-decoration-none p-0" onClick={() => {
                if (key === 'followUp') setColumnFilters(f => ({ ...f, followFrom: '', followTo: '' }));
                else setColumnFilter(key, '');
              }}>Clear</button>
              <button type="button" className="btn btn-link btn-sm text-decoration-none p-0" onClick={() => setActiveHeaderFilter('')}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const openCreate = () => {
    setForm({ ...blankForm, enquiryDate: new Date().toISOString().split('T')[0] });
    setEditTarget(null);
    setShowForm(true);
  };
  const openEdit = (e) => {
    const linkedCourseId = e.course?._id || '';
    const hasFreeText = !linkedCourseId && !!e.courseName;
    setForm({
      enquiryDate:   e.enquiryDate ? new Date(e.enquiryDate).toISOString().split('T')[0] : '',
      name:          e.name || '',
      area:          e.area || '',
      address:       e.address || '',
      phone:         e.phone || '',
      email:         e.email || '',
      courseId:       hasFreeText ? '__other__' : linkedCourseId,
      courseName:     e.courseName || '',
      source:         e.source || 'walk_in',
      enrollStatus:   e.enrollStatus || 'interested',
      joined:         e.joined || false,
      conversation:   e.conversation || '',
      conselledBy:    e.conselledBy?._id || '',
      conselledByOther: e.conselledByOther || '',
      degree:        e.degree || '',
      department:    e.department || '',
      collegeName:   e.collegeName || '',
      personArrived: e.personArrived || 'student',
      notes:         e.notes || '',
      nextFollowUp:  e.nextFollowUp ? new Date(e.nextFollowUp).toISOString().split('T')[0] : '',
    });
    setEditTarget(e);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showAlert('Name is required.','danger'); return; }
    const isOther = form.courseId === '__other__';
    if (isOther && !form.courseName.trim()) {
      showAlert('Please enter the course name for the "Other" option.', 'danger');
      return;
    }
    const isConselledByOther = form.conselledBy === 'other';
    const payload = {
      ...form,
      courseId: isOther ? '' : form.courseId,
      conselledBy: isConselledByOther ? '' : form.conselledBy,
    };
    setSaving(true);
    try {
      let r;
      if (editTarget) r = await api.put(`/training/enquiries/${editTarget._id}`, payload);
      else            r = await api.post('/training/enquiries', payload);
      if (r.data?.success !== false) {
        showAlert(editTarget?'Updated.':'Enquiry created.','success');
        setShowForm(false);
        fetchEnquiries();
        fetchYearStats();
        refreshStats();
        api.get('/training/enquiries/meta/counsellors').then(r=>setCounsellors(Array.isArray(r.data?.records)?r.data.records:Array.isArray(r.data?.data)?r.data.data:[])).catch(()=>{});
      } else showAlert(r.data?.message||'Failed.','danger');
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };

  const handleStatusChange = async (enq, status) => {
    try {
      await api.patch(`/training/enquiries/${enq._id}/status`, { status });
      setEnquiries(prev => prev.map(e => e._id===enq._id ? {...e,status} : e));
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  const handleDelete = async (enq) => {
    if (!window.confirm(`Delete enquiry for "${enq.name}"?`)) return;
    try {
      await api.delete(`/training/enquiries/${enq._id}`);
      showAlert('Deleted.','success');
      fetchEnquiries();
      fetchYearStats();
      refreshStats();
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  const handleFollowUpSuccess = (updated) => {
    setEnquiries(prev => prev.map(e => e._id===updated._id ? {...e,...updated} : e));
    showAlert('Follow-up recorded.','success');
  };

  const handleEnrollFromEnquiry = (enq) => {
    const params = new URLSearchParams({
      from:      'enquiry',
      enquiryId: enq._id,
      name:      enq.name || '',
      phone:     enq.phone || '',
    });
    const courseId = enq.course?._id || enq.course || '';
    if (courseId) params.set('courseId', courseId);
    navigate(`/enrollments?${params.toString()}`);
  };

  return (
    <div className="container-fluid py-3" style={{ maxWidth: '98%' }}>
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={()=>setAlert(p=>({...p,show:false}))} />

      <PageHeader
        title="Enquiries"
        icon="bi-chat-square-text"
        subtitle="Track leads, follow-ups & conversions"
        actions={(canCreate || canSeeAnalytics) && (
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-outline-primary btn-sm" onClick={() => setDlgImport(true)}>
              <i className="bi bi-upload me-1"/>Import
            </button>
            {canCreate && (
              <button className="btn btn-primary btn-sm" onClick={openCreate}>
                <i className="bi bi-plus-lg me-1"/>New Enquiry
              </button>
            )}
          </div>
        )}
      />

      {canSeeAnalytics && (
        <div className="d-flex justify-content-end mb-3">
          <ViewToggle
            storageKey="enquiries"
            value={view}
            onChange={setView}
          />
        </div>
      )}

      {canSeeAnalytics && view === 'analytics' && (
        <div className="mb-4">
          <EnquiriesAnalytics />
        </div>
      )}

      {(!canSeeAnalytics || view === 'table') && stats && (
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-3">
            <div className="card h-100 border-light">
              <div className="card-body py-2 px-3 d-flex flex-column justify-content-center">
                <div className="text-muted small">Today's Enquiries</div>
                <div className="fs-5 fw-bold text-dark mt-1">
                  {stats.todayCount || 0}
                </div>
                <div className="small text-primary fw-semibold text-truncate" title={stats.todayTopCourse?.courseName || 'No enquiries today'}>
                  {stats.todayTopCourse?.courseName || 'No enquiries today'}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3">
            <div className="card h-100 border-light">
              <div className="card-body py-2 px-3">
                <div className="text-muted small mb-2">Enquiry Window</div>
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Weekly</div>
                    <div className="fs-5 fw-bold text-dark">{stats.weeklyCount || 0}</div>
                  </div>
                  <div className="vr" />
                  <div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Monthly</div>
                    <div className="fs-5 fw-bold text-dark">{stats.monthlyCount || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-2"><StatCard title="Total Enquiries" value={stats.total} icon="bi-people" color="primary"/></div>
          <div className="col-6 col-md-2"><StatCard title="Following" value={stats.contacted} icon="bi-telephone" color="warning"/></div>
          <div className="col-6 col-md-2"><StatCard title="Enrolled" value={stats.enrolled} icon="bi-person-check" color="success"/></div>
          <div className="col-6 col-md-2">
            <div className="card h-100">
              <div className="card-body py-2 px-3">
                <div className="text-muted small">Conversion</div>
                <div className="fs-4 fw-bold">{stats.total>0?Math.round((stats.enrolled/stats.total)*100):0}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <BSModal show={showForm} onClose={()=>setShowForm(false)} title={editTarget?'Edit Enquiry':'New Enquiry'} size="modal-xl"
          footer={<>
            <button className="btn btn-secondary btn-sm" onClick={()=>setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving?<><span className="spinner-border spinner-border-sm me-1"/>Saving…</>:editTarget?'Save Changes':'Create Enquiry'}
            </button>
          </>}>
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="border rounded-3 p-3 h-100" style={{ background:'#f8fafc' }}>
                <div className="fw-semibold mb-3">Basic Info</div>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-12"><label className="form-label">Date</label>
                    <input className="form-control form-control-sm" type="date" value={form.enquiryDate} onChange={e=>setF('enquiryDate',e.target.value)} />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Name <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={form.name} onChange={e=>setF('name',e.target.value)} autoFocus />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Contact Number (WhatsApp)</label>
                    <input className="form-control form-control-sm" value={form.phone} onChange={e=>setF('phone',e.target.value)} placeholder="Primary contact" />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Email Address</label>
                    <input className="form-control form-control-sm" type="email" value={form.email} onChange={e=>setF('email',e.target.value)} placeholder="Student email" />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Area</label>
                    <input className="form-control form-control-sm" value={form.area} onChange={e=>setF('area',e.target.value)} placeholder="e.g. Tambaram, Chennai" />
                  </div>
                  <div className="col-12"><label className="form-label">Address</label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.address} onChange={e=>setF('address',e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="border rounded-3 p-3 h-100" style={{ background:'#fcfcff' }}>
                <div className="fw-semibold mb-3">Course & Counselling</div>
                <div className="row g-3">
                  <div className="col-12"><label className="form-label">Course Interest</label>
                    <select
                      className="form-select form-select-sm"
                      value={form.courseId}
                      onChange={e => {
                        const v = e.target.value;
                        setF('courseId', v);
                        if (v !== '__other__') setF('courseName', '');
                      }}
                    >
                      <option value="">Select Course</option>
                      {courses.map(c => (
                        <option key={c._id} value={c._id}>{c.courseName || c.name}</option>
                      ))}
                      <option value="__other__">Other (specify below)</option>
                    </select>
                    {form.courseId === '__other__' && (
                      <input className="form-control form-control-sm mt-2" placeholder="Type the course name…" value={form.courseName} onChange={e => setF('courseName', e.target.value)} />
                    )}
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Source</label>
                    <select className="form-select form-select-sm" value={form.source} onChange={e=>setF('source',e.target.value)}>
                      {SOURCES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Counselled By</label>
                    <select className="form-select form-select-sm" value={form.conselledBy} onChange={e=>setF('conselledBy',e.target.value)}>
                      <option value="">Select Counsellor</option>
                      {counsellors.map(c => (
                        <option key={c._id} value={c._id}>{c.firstName} {c.lastName} ({c.roleKey})</option>
                      ))}
                      <option value="other">Other</option>
                    </select>
                    {form.conselledBy === 'other' && (
                      <input className="form-control form-control-sm mt-2" placeholder="Enter name…" value={form.conselledByOther} onChange={e => setF('conselledByOther', e.target.value)} />
                    )}
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Enroll Status</label>
                    <select className="form-select form-select-sm" value={form.enrollStatus} onChange={e=>setF('enrollStatus',e.target.value)}>
                      {ENROLL_STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Joined</label>
                    <select className="form-select form-select-sm" value={form.joined.toString()} onChange={e=>setF('joined',e.target.value==='true')}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="border rounded-3 p-3 h-100" style={{ background:'#fffdf7' }}>
                <div className="fw-semibold mb-3">Background & Follow-up</div>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-12"><label className="form-label">Person Arrived</label>
                    <select className="form-select form-select-sm" value={form.personArrived} onChange={e=>setF('personArrived',e.target.value)}>
                      {PERSON_ARRIVED.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Degree</label>
                    <input className="form-control form-control-sm" value={form.degree} onChange={e=>setF('degree',e.target.value)} placeholder="e.g. B.Sc, B.Com, B.A" />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Department</label>
                    <input className="form-control form-control-sm" value={form.department} onChange={e=>setF('department',e.target.value)} />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">College Name</label>
                    <input className="form-control form-control-sm" value={form.collegeName} onChange={e=>setF('collegeName',e.target.value)} />
                  </div>
                  <div className="col-md-6 col-lg-12"><label className="form-label">Next Follow-up</label>
                    <input className="form-control form-control-sm" type="date" value={form.nextFollowUp} onChange={e=>setF('nextFollowUp',e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="row g-3">
                <div className="col-lg-8"><label className="form-label">Conversation</label>
                  <textarea className="form-control form-control-sm" rows={4} value={form.conversation} onChange={e=>setF('conversation',e.target.value)} placeholder="Notes from the conversation…" />
                </div>
                <div className="col-lg-4"><label className="form-label">Internal Notes</label>
                  <textarea className="form-control form-control-sm" rows={4} value={form.notes} onChange={e=>setF('notes',e.target.value)} placeholder="Counsellor notes, reminders, special context…" />
                </div>
              </div>
            </div>
          </div>
        </BSModal>
      )}

      {(!canSeeAnalytics || view === 'table') && (
      <>
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <div className="input-group input-group-sm">
            <span className="input-group-text"><i className="bi bi-search"/></span>
            <input className="form-control" placeholder="Search name, phone, area…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
          </div>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filterStatus} onChange={e=>{setFilterStatus(e.target.value);setPage(1);}}>
            <option value="">All Statuses</option>
            {STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filterSource} onChange={e=>{setFilterSource(e.target.value);setPage(1);}}>
            <option value="">All Sources</option>
            {SOURCES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <div className="form-check form-switch mt-1">
            <input className="form-check-input" type="checkbox" id="dueSoon" checked={dueSoon} onChange={e=>{setDueSoon(e.target.checked);setPage(1);}}/>
            <label className="form-check-label small" htmlFor="dueSoon">Due soon (3 days)</label>
          </div>
        </div>
      </div>

      {/* Year Filter Tabs */}
      <div className="d-flex gap-1 mb-2 mt-3 align-items-center flex-wrap">
        <span className="me-2 small text-muted">Year:</span>
        <button
          className={`btn btn-sm ${yearFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => { setYearFilter('all'); setMonthFilter('all'); setPage(1); }}
        >
          All {yearStats?.total ? `(${yearStats.total})` : ''}
        </button>
        {yearStats?.years && Object.entries(yearStats.years)
          .sort((a, b) => Number(b[0]) - Number(a[0]))
          .map(([yr, data]) => (
            <button
              key={yr}
              className={`btn btn-sm ${yearFilter === yr ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => { setYearFilter(yr); setMonthFilter('all'); setPage(1); }}
            >
              {yr} ({data.count})
            </button>
          ))
        }
        {yearFilter !== 'all' && (
          <>
            <span className="ms-3 me-2 small text-muted">Month:</span>
            <button
              className={`btn btn-sm ${monthFilter === 'all' ? 'btn-info' : 'btn-outline-info'}`}
              onClick={() => { setMonthFilter('all'); setPage(1); }}
            >
              All
            </button>
            {MONTHS.map(mo => {
              const count = yearStats?.years?.[yearFilter]?.months?.[mo.value] || 0;
              if (count === 0 && yearFilter !== 'all') return null; // Hide empty months
              return (
                <button
                  key={mo.value}
                  className={`btn btn-sm ${monthFilter === mo.value ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => { setMonthFilter(mo.value); setPage(1); }}
                >
                  {mo.label} {count > 0 ? `(${count})` : ''}
                </button>
              );
            })}
          </>
        )}
        <div className="ms-auto">
          {canAdmin && (
            <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteAll} title="Delete all">
              <i className="bi bi-trash me-1"/>Delete All
            </button>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"/></div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="text-center py-5 text-muted"><i className="bi bi-chat-square fs-1 d-block mb-2"/>No enquiries found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '28%', padding: '8px 10px' }}>{renderHeaderFilter('lead', 'Lead', 'datalist', enquiryColumnOptions.lead)}</th>
                    <th style={{ width: '24%', padding: '8px 10px' }}>{renderHeaderFilter('courseSource', 'Course / Source', 'datalist', enquiryColumnOptions.courseSource)}</th>
                    <th style={{ width: '18%', padding: '8px 10px' }}>{renderHeaderFilter('pipeline', 'Pipeline', 'datalist', enquiryColumnOptions.pipeline)}</th>
                    <th style={{ width: '20%', padding: '8px 10px' }}>{renderHeaderFilter('followUp', 'Follow-up', 'dateRange')}</th>
                    <th className="text-end" style={{ width: '10%', whiteSpace: 'nowrap', padding: '8px 10px' }}>
                      <button className="btn btn-outline-secondary btn-sm" type="button" onClick={clearColumnFilters}>Clear</button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map(enq => (
                    <tr key={enq._id}>
                      <td className="py-2" style={{ padding: '8px 10px' }}>
                        <div className="fw-semibold text-truncate">{enq.name}</div>
                        <div className="small text-muted text-truncate">
                          {enq.phone || '—'}{enq.area ? ` · ${enq.area}` : ''}
                        </div>
                        {enq.email && <div className="small text-muted text-truncate">{enq.email}</div>}
                        <div className="small text-muted text-truncate">
                          {fmtDate(enq.enquiryDate)} · {enq.enquiryNumber}
                        </div>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <CourseNameBadge courseName={enq.course?.courseName || enq.courseName || '—'} courseColor={enq.course?.courseColor} className="text-truncate" compact />
                        <div className="small text-muted text-truncate">{enq.collegeName || enq.degree || '—'}</div>
                        <div className="small text-muted text-truncate">
                          {SOURCES.find(s=>s.value===enq.source)?.label||enq.source}
                        </div>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div className="d-flex flex-column gap-1">
                          <span className={`badge bg-${statusColor(enq.status)}`} style={{ fontSize: 11 }}>{statusLabel(enq.status)}</span>
                          <span className={`badge bg-${enq.enrollStatus==='interested'?'success':enq.enrollStatus==='not_interested'?'secondary':enq.enrollStatus==='waiting'?'warning':'info'}`} style={{ fontSize: 11 }}>
                            {ENROLL_STATUSES.find(e=>e.value===enq.enrollStatus)?.label||enq.enrollStatus}
                          </span>
                          <span className={`badge bg-${enq.joined?'success':'secondary'}`} style={{ fontSize: 11, width: 'fit-content' }}>
                            {enq.joined ? 'Joined' : 'Not Joined'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div className={`small text-truncate ${isOverdue(enq.nextFollowUp)?'text-danger fw-bold':isDueSoon(enq.nextFollowUp)?'text-warning fw-bold':'text-muted'}`}>
                          {isOverdue(enq.nextFollowUp) && <i className="bi bi-exclamation-triangle me-1"/>}
                          {fmtDate(enq.nextFollowUp)}
                        </div>
                        <div className="small text-muted text-truncate">{enq.conselledBy?.firstName?`${enq.conselledBy.firstName} ${enq.conselledBy.lastName}`:enq.conselledByOther||'—'}</div>
                        <div className="small text-muted text-truncate">{enq.personArrived ? `Arrived: ${PERSON_ARRIVED.find(p => p.value===enq.personArrived)?.label || enq.personArrived}` : '—'}</div>
                      </td>
                      <td className="text-end" style={{whiteSpace:'nowrap', padding: '8px 10px' }}>
                        <button className="btn btn-outline-secondary btn-sm me-1" style={{ padding: '0.2rem 0.45rem' }} title="View Detail" onClick={()=>setDlgDetail(enq)}>
                          <i className="bi bi-eye"/>
                        </button>
                        {canEdit && (
                          <>
                            {enq.status !== 'enrolled' && (
                              <button className="btn btn-success btn-sm me-1" style={{ padding: '0.2rem 0.45rem' }} title="Enroll" onClick={()=>handleEnrollFromEnquiry(enq)}>
                                <i className="bi bi-person-plus"/>
                              </button>
                            )}
                            <button className="btn btn-outline-success btn-sm me-1" style={{ padding: '0.2rem 0.45rem' }} title="Add Follow-up" onClick={()=>setDlgFollowUp(enq)}>
                              <i className="bi bi-telephone-plus"/>
                            </button>
                            <button className="btn btn-outline-primary btn-sm me-1" style={{ padding: '0.2rem 0.45rem' }} title="Edit" onClick={()=>openEdit(enq)}>
                              <i className="bi bi-pencil"/>
                            </button>
                          </>
                        )}
                        {canAdmin && (
                          <button className="btn btn-outline-danger btn-sm" style={{ padding: '0.2rem 0.45rem' }} title="Delete" onClick={()=>handleDelete(enq)}>
                            <i className="bi bi-trash"/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          total={(Math.max(totalPages - 1, 0) * 20) + enquiries.length}
          limit={20}
        />
      )}

      {false && totalPages > 1 && (
        <nav className="mt-3 d-flex justify-content-center">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page<=1?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.max(1,p-1))}>‹</button></li>
            {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
              <li key={p} className={`page-item ${p===page?'active':''}`}><button className="page-link" onClick={()=>setPage(p)}>{p}</button></li>
            ))}
            <li className={`page-item ${page>=totalPages?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>›</button></li>
          </ul>
        </nav>
      )}
      </>
      )}

      <FollowUpDialog
        show={!!dlgFollowUp}
        enquiry={dlgFollowUp}
        onClose={()=>setDlgFollowUp(null)}
        onSuccess={handleFollowUpSuccess}
        onAlert={showAlert}
      />

      <DetailDialog
        show={!!dlgDetail}
        enquiry={dlgDetail}
        onClose={()=>setDlgDetail(null)}
      />

      <ImportDialog
        show={!!dlgImport}
        onClose={()=>setDlgImport(null)}
        onSuccess={() => { showAlert('Enquiries imported successfully!','success'); fetchEnquiries(); fetchYearStats(); refreshStats(); }}
      />
    </div>
  );
}
